import React, { useCallback, useEffect, useState } from 'react';
import { Card, Table, Tag, Space, Switch, Button, Tooltip, Typography, Descriptions, Alert, Empty, message } from 'antd';
import { ReloadOutlined, ShareAltOutlined } from '@ant-design/icons';
import { useTranslation } from '../../../shared/i18n';
import cdnProbeService from '../../services/cdnProbeService';

const { Text } = Typography;
const fmtTs = (s) => (s ? new Date(s * 1000).toLocaleString() : '-');
const BGP_COLOR = { announcing: 'success', withdrawn: 'warning', session_down: 'error', bird_down: 'error', no_report: 'default' };

/** anycast 总览:集群 anycast IP / BGP 节点实时状态 / DNS 判定;只改 DNS 开关,不动 BGP 宣告 */
const Anycast = () => {
    const { t } = useTranslation();
    const [data, setData] = useState({ clusters: [], env: {} });
    const [loading, setLoading] = useState(false);

    const load = useCallback(async () => {
        setLoading(true);
        const r = await cdnProbeService.getAnycast();
        setLoading(false);
        if (r.success) setData(r.data || { clusters: [], env: {} });
        else message.error(r.error || t('Failed to load'));
    }, [t]);

    useEffect(() => { load(); }, [load]);
    useEffect(() => {
        const id = setInterval(load, 20000);
        return () => clearInterval(id);
    }, [load]);

    const setCluster = async (c, on) => {
        const r = await cdnProbeService.setClusterAnycastDns(c.id, on);
        if (r.success) load(); else message.error(r.error || t('Save failed'));
    };
    const setLine = async (l, off) => {
        const r = await cdnProbeService.setLineAnycastOff(l.id, off);
        if (r.success) load(); else message.error(r.error || t('Save failed'));
    };

    const statusLabel = { announcing: t('Announcing'), withdrawn: t('Withdrawn'), session_down: t('Session down'), bird_down: t('BIRD down'), no_report: t('No report') };

    const nodeColumns = [
        { title: t('Node'), dataIndex: 'name', render: (n, r) => <Space size={4}>{n}{r.status !== 'active' && <Tag>{r.status}</Tag>}</Space> },
        {
            title: 'BGP', dataIndex: 'bgp_status',
            render: (s, r) => (
                <Space size={4} wrap>
                    <Tag color={BGP_COLOR[s] || 'default'}>{statusLabel[s] || s}</Tag>
                    {r.config_synced === false && <Tag color="warning">{t('Config not synced')}</Tag>}
                </Space>
            ),
        },
        { title: t('Edge healthy'), dataIndex: 'dns_healthy', render: (v) => (v ? <Tag color="success">✓</Tag> : <Tag color="error">×</Tag>) },
        {
            title: t('Sessions'), dataIndex: 'sessions',
            render: (ss) => (ss?.filter((x) => x.proto === 'BGP').length
                ? <Space direction="vertical" size={0}>{ss.filter((x) => x.proto === 'BGP').map((x) => (
                    <Text key={x.name} type={/established/i.test(x.info || x.state) ? undefined : 'danger'} style={{ fontSize: 12 }}>
                        {x.name}: {x.info || x.state}{x.exported !== null && x.exported !== undefined ? ` · ${t('exported')} ${x.exported}` : ''}{x.since ? ` · ${x.since}` : ''}
                    </Text>
                ))}</Space>
                : <Text type="secondary">-</Text>),
        },
        {
            title: t('BGP config'), dataIndex: 'bgp_config',
            render: (c) => (c
                ? <Text style={{ fontSize: 12 }}>AS{c.local_asn} → AS{c.peer_asn} · {c.neighbor}{c.neighbor6 ? ` / ${c.neighbor6}` : ''}{c.password ? ` · ${t('password')} ${c.password}` : ''}</Text>
                : '-'),
        },
        { title: t('Last report'), dataIndex: 'reported_at', render: fmtTs },
    ];

    const lineColumns = [
        { title: t('Line'), dataIndex: 'label' },
        {
            title: 'DNS', key: 'use',
            render: (_, l) => (
                <Tooltip title={l.why}>
                    {l.would_use_anycast ? <Tag color="success">anycast</Tag> : <Tag>{t('Unicast')}</Tag>}
                </Tooltip>
            ),
        },
        {
            title: t('Force off'), key: 'off',
            render: (_, l) => (
                <Space size={4}>
                    <Switch size="small" checked={l.anycast_off} onChange={(v) => setLine(l, v)} />
                    {l.env_off && <Tag>env</Tag>}
                </Space>
            ),
        },
    ];

    return (
        <Card
            title={<Space><ShareAltOutlined />Anycast</Space>}
            extra={<Button size="small" icon={<ReloadOutlined />} onClick={load} loading={loading} />}
        >
            <Alert type="info" showIcon style={{ marginBottom: 16 }} message={t('BGP announcements are read-only here.')} />
            {!loading && data.clusters.length === 0 && <Empty />}
            <Space direction="vertical" style={{ width: '100%' }} size={16}>
                {data.clusters.map((c) => (
                    <Card key={c.id} size="small"
                        title={
                            <Space wrap>
                                <strong>{c.name}</strong>
                                {c.status !== 'active' && <Tag>{c.status}</Tag>}
                                <Tooltip title={c.decision.reason}>
                                    <Tag color={c.decision.use_anycast ? 'success' : 'default'}>
                                        {c.decision.use_anycast ? t('Anycast ready') : t('Anycast not ready')}
                                    </Tag>
                                </Tooltip>
                            </Space>
                        }
                        extra={
                            <Space>
                                <Text type="secondary">{t('Use anycast in DNS')}</Text>
                                <Switch size="small" checked={c.anycast_dns} onChange={(v) => setCluster(c, v)}
                                    disabled={!c.anycast_ipv4 && !c.anycast_ipv6} />
                            </Space>
                        }
                    >
                        <Descriptions size="small" column={{ xs: 1, md: 3 }} style={{ marginBottom: 8 }}>
                            <Descriptions.Item label="IPv4">{c.anycast_ipv4 || '-'}</Descriptions.Item>
                            <Descriptions.Item label="IPv6">{c.anycast_ipv6 || '-'}</Descriptions.Item>
                            <Descriptions.Item label={t('Prefixes')}>{c.prefixes.length ? c.prefixes.join(', ') : '-'}</Descriptions.Item>
                            <Descriptions.Item label={t('Nodes')}>{c.node_count} · BGP {c.bgp_nodes.length}</Descriptions.Item>
                            <Descriptions.Item label={t('Decision')}>{c.decision.reason}</Descriptions.Item>
                        </Descriptions>
                        {c.bgp_nodes.length > 0 && (
                            <Table size="small" rowKey="id" columns={nodeColumns} dataSource={c.bgp_nodes} pagination={false} scroll={{ x: 'max-content' }} />
                        )}
                        {c.lines.length > 0 && (
                            <Table size="small" rowKey="id" columns={lineColumns} dataSource={c.lines} pagination={false} style={{ marginTop: 8 }} />
                        )}
                    </Card>
                ))}
            </Space>
        </Card>
    );
};

export default Anycast;
